import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DROPDOWN_Z } from './ModalPortal';

// A select you can type into. Drop-in for CustomSelect and
// CustomSelectForPrescrption: same props, same { target: { name, value } }
// change event. The menu renders in a portal so a card with overflow hidden
// cannot clip it, matching the suggestion lists already used on prescriptions.

const SIZES = {
  md: {
    label: 'block mb-1 text-xs md:text-sm font-medium text-gray-700',
    input: 'w-full rounded-md border px-3 py-2 pr-16 text-xs md:text-sm text-gray-800 bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition',
    invalid: 'border-red-400 focus:ring-red-300',
    off: 'bg-gray-100 cursor-not-allowed',
    wrapper: 'mt-2 w-full',
    error: 'mt-1 block text-xs text-red-500',
  },
  sm: {
    label: 'block mb-1 text-xs font-semibold text-slate-600',
    input: 'w-full rounded-xl border border-slate-200 hover:border-slate-300 bg-white pl-3 pr-14 py-1.5 text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition shadow-sm',
    invalid: 'border-rose-400 focus:ring-rose-200',
    off: 'bg-slate-100 text-slate-400 cursor-not-allowed',
    wrapper: 'w-full',
    error: 'mt-1 block text-xs text-rose-500',
  },
};

const MENU_HEIGHT = 260;

const normalise = (text) => String(text ?? '').toLowerCase().trim();

// Prefix matches first, then anything containing the query - typing "nur"
// should put "Nurse" above "Staff Nurse / Nursing Assistant".
const rank = (options, query) => {
  const q = normalise(query);
  if (!q) return options;

  const starts = [];
  const contains = [];
  for (const option of options) {
    const haystack = normalise(option);
    if (haystack.startsWith(q)) starts.push(option);
    else if (haystack.includes(q)) contains.push(option);
  }
  return [...starts, ...contains];
};

const SearchableSelect = ({
  label,
  options,
  onChange,
  value,
  name,
  placeholder,
  error,
  id,
  disabled,
  required = false,
  size = 'md',
  className = '',
  allowCustom = false,
  emptyMessage = 'No matches',
  // Cap on rendered matches, for option lists in the thousands.
  maxResults,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [position, setPosition] = useState(null);
  const inputEl = useRef(null);
  const menuEl = useRef(null);
  const blurTimer = useRef(null);

  const styles = SIZES[size] || SIZES.md;
  const list = useMemo(() => (options || []).filter((option) => option !== '' && option != null), [options]);
  const matches = useMemo(() => {
    if (!open) return [];
    const ranked = rank(list, query);
    return maxResults ? ranked.slice(0, maxResults) : ranked;
  }, [open, list, query, maxResults]);

  const syncPosition = useCallback(() => {
    const el = inputEl.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const showAbove = spaceBelow < MENU_HEIGHT && spaceAbove > spaceBelow;
    const maxHeight = Math.min(MENU_HEIGHT, Math.max(120, (showAbove ? spaceAbove : spaceBelow) - 20));

    setPosition({
      top: showAbove ? Math.max(10, rect.top - maxHeight - 4) : rect.bottom + 2,
      left: Math.max(10, Math.min(rect.left, window.innerWidth - rect.width - 10)),
      width: rect.width,
      maxHeight,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    syncPosition();
    const handleViewportChange = () => syncPosition();
    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);
    return () => {
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [open, syncPosition]);

  // Keep the highlight on the selected option when the menu opens, so Enter
  // straight after opening is a no-op rather than a surprise change.
  useEffect(() => {
    if (!open) return;
    const current = matches.findIndex((option) => option === value);
    setHighlight(current === -1 ? 0 : current);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  // Keep the arrow-key highlight inside the scrolled menu.
  useEffect(() => {
    if (!open) return;
    const option = menuEl.current?.querySelector(`[data-index="${highlight}"]`);
    if (typeof option?.scrollIntoView === 'function') option.scrollIntoView({ block: 'nearest' });
  }, [open, highlight, position]);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const emit = (next) => onChange?.({ target: { name, value: next, id } });

  const openMenu = () => {
    if (disabled) return;
    clearTimeout(blurTimer.current);
    syncPosition();
    setOpen(true);
  };

  const closeMenu = () => {
    setOpen(false);
    setQuery('');
  };

  const pick = (option) => {
    closeMenu();
    emit(option);
  };

  // Typing is a search, not an edit, unless the field accepts free text.
  const handleTyping = (event) => {
    const typed = event.target.value;
    setQuery(typed);
    openMenu();
    if (allowCustom) emit(typed);
  };

  const handleBlur = () => {
    blurTimer.current = setTimeout(() => {
      // A half-typed search is discarded; a free-text field keeps what was typed.
      if (allowCustom && query && query !== value) emit(query);
      closeMenu();
    }, 150);
  };

  const handleKeyDown = (event) => {
    if (open && matches.length) {
      if (event.key === 'ArrowDown') {
        setHighlight((prev) => (prev + 1) % matches.length);
        return event.preventDefault();
      }
      if (event.key === 'ArrowUp') {
        setHighlight((prev) => (prev - 1 + matches.length) % matches.length);
        return event.preventDefault();
      }
      if (event.key === 'Enter' && matches[highlight]) {
        pick(matches[highlight]);
        return event.preventDefault();
      }
    }
    if (event.key === 'Escape' && open) {
      closeMenu();
      return event.preventDefault();
    }
    if (event.key === 'ArrowDown' && !open) {
      openMenu();
      return event.preventDefault();
    }
    return undefined;
  };

  return (
    <div className={`${styles.wrapper} ${className}`}>
      {label && (
        <label htmlFor={id || name} className={styles.label}>
          {label}
          {required && <span className="ml-1 text-rose-500">*</span>}
        </label>
      )}

      <div className="relative w-full">
        <input
          id={id || name}
          name={name}
          type="text"
          role="combobox"
          aria-expanded={open}
          autoComplete="off"
          disabled={disabled}
          required={required}
          placeholder={open && value ? value : placeholder}
          value={open ? query : (value || '')}
          ref={inputEl}
          onChange={handleTyping}
          onFocus={openMenu}
          onClick={openMenu}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`${styles.input} ${disabled ? styles.off : ''} ${error ? styles.invalid : ''}`}
        />

        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-1 pr-2 text-slate-400">
          {value && !disabled && (
            <button
              type="button"
              title="Clear"
              className="pointer-events-auto rounded p-0.5 text-slate-400 transition hover:text-slate-600"
              onMouseDown={(event) => {
                event.preventDefault();
                emit('');
                setQuery('');
              }}
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {open && position
        ? createPortal(
          <div
            ref={menuEl}
            className={`fixed ${DROPDOWN_Z} overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg`}
            style={{ top: position.top, left: position.left, width: position.width, maxHeight: position.maxHeight }}
            onMouseDown={(event) => event.preventDefault()}
          >
            {matches.length ? (
              <ul>
                {matches.map((option, index) => (
                  <li key={`${option}-${index}`}>
                    <button
                      type="button"
                      data-index={index}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        pick(option);
                      }}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
                        index === highlight ? 'bg-primary-50' : 'bg-white hover:bg-gray-50'
                      } ${option === value ? 'font-semibold text-primary-700' : 'text-slate-700'}`}
                    >
                      <span className="truncate">{option}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-3 py-2 text-xs text-slate-400">{emptyMessage}</p>
            )}
          </div>,
          document.body
        )
        : null}

      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
};

export default SearchableSelect;
